import type { MessageSharePresentationDto } from "@multi-chat/shared";
import { Film, Image as ImageIcon, Circle } from "lucide-react";
import { useTranslation } from "react-i18next";
import { cn } from "../../../lib/utils.js";
import { MessageReplyPreviewThumbnail } from "../MessageReplyPreview/MessageReplyPreviewThumbnail.js";
import { shareTypeLabel } from "../share-label.js";

function shareIcon(shareType: string): React.JSX.Element {
  switch (shareType) {
    case "ig_reel":
    case "reel":
      return <Film size={16} className="shrink-0 text-ink-subtle" aria-hidden="true" />;
    case "ig_story":
    case "story":
      return <Circle size={16} className="shrink-0 text-ink-subtle" aria-hidden="true" />;
    default:
      return <ImageIcon size={16} className="shrink-0 text-ink-subtle" aria-hidden="true" />;
  }
}

export function MessageShareCard({
  presentation,
  organizationId,
  className,
}: {
  presentation: MessageSharePresentationDto;
  organizationId: number;
  className?: string;
}): React.JSX.Element {
  const { t } = useTranslation();
  const previewAttachment = presentation.previewAttachment;
  const showThumbnail =
    previewAttachment != null &&
    (previewAttachment.storageState === "STORED" ||
      previewAttachment.storageState === "PENDING_STORAGE");

  return (
    <div
      className={cn(
        "my-1 w-full max-w-[18rem] min-w-0 overflow-hidden rounded-lg border border-line/80 bg-surface/80",
        className,
      )}
      data-testid="message-share-card"
    >
      <div className="flex items-center gap-2 border-b border-line/60 px-2.5 py-1.5">
        {shareIcon(presentation.shareType)}
        <p className="m-0 truncate text-[0.75rem] font-semibold leading-snug text-ink">
          {shareTypeLabel(presentation.shareType, t)}
        </p>
      </div>
      <div className="flex min-w-0 items-start gap-2 px-2.5 py-2">
        <div className="min-w-0 flex-1 space-y-1">
          {presentation.username ? (
            <p className="m-0 truncate text-[0.8125rem] font-medium leading-snug text-ink">
              {presentation.username}
            </p>
          ) : null}
          {presentation.captionSnippet ? (
            <p className="m-0 line-clamp-3 text-[0.8125rem] leading-snug text-ink-muted">
              {presentation.captionSnippet}
            </p>
          ) : null}
        </div>
        {showThumbnail ? (
          <MessageReplyPreviewThumbnail
            organizationId={organizationId}
            attachment={{
              ...previewAttachment,
              mimeType: previewAttachment.mimeType ?? null,
            }}
            className="size-14 shrink-0"
          />
        ) : null}
      </div>
    </div>
  );
}
